import { g, layers } from '@hcie/core';
import { historyManager, DrawAction } from '@hcie/core';
import { renderLayers, updateLayerPanel } from '@hcie/canvas-ui';
import { api } from '@hcie/io';
import { ProjectIO } from '@hcie/io';
import { newDocument } from '@hcie/core';
import * as filters from '@hcie/canvas-ui';

/**
 * Menu Handlers
 * This module provides global functions that are called by onclick attributes in index.html.
 * It bridges the modular TypeScript codebase with the legacy HTML-based UI.
 */

// --- File Operations ---

export async function handleOpenFile() {
    try {
        const filePath = await api.openFile();
        if (!filePath) return;

        console.log('[handleOpenFile] File selected:', filePath);
        
        const buffer = await api.readFileBinary(filePath);
        if (!buffer) {
            alert('Could not read file binary data.');
            return;
        }

        console.log('[handleOpenFile] Binary data ready. Importing...');
        const success = await ProjectIO.importImage(buffer.buffer as ArrayBuffer, filePath);
        
        if (success) {
            g.filepath = filePath;
            const el = document.getElementById('filePath');
            if (el) el.innerText = filePath;
            console.log('[handleOpenFile] Import successful.');
        }
    } catch (err) {
        console.error('[handleOpenFile] Error:', err);
        alert(`Failed to open file: ${(err as Error).message}\nCheck console for details.`);
    }
}

export async function handleSaveFile() {
    try {
        if (!g.filepath || g.filepath.toLowerCase().endsWith('.png') || g.filepath.toLowerCase().endsWith('.jpg') || g.filepath.toLowerCase().endsWith('.gif')) {
            // If no path or path is an image (can't save project as image directly), do Save As
            return handleSaveAsFile();
        }

        const content = await ProjectIO.saveProject();
        const savedPath = await api.saveFile(content, g.filepath, false, 'hcie');
        if (savedPath) {
            console.log('File saved to:', savedPath);
            g.filepath = savedPath;
            const el = document.getElementById('filePath');
            if (el) el.innerText = savedPath;
        }
    } catch (err) {
        console.error('Save failed:', err);
    }
}

export async function handleSaveAsFile() {
    try {
        // 1. Ask for Format
        const format = await (window as any).DialogHandler.showFormatSelector() as 'hcie' | 'png' | 'psd' | 'gif' | null;
        if (!format) return; // User cancelled

        let content: string | Uint8Array;
        let type: string = format;

        // 2. Export/Save using generalized routine
        const extension = "." + type;
        const outContent = await ProjectIO.exportImage(extension);

        // 3. Save File
        const savedPath = await api.saveFile(outContent, g.filepath, true, type);
        if (savedPath) {
            console.log(`File saved as ${format} to:`, savedPath);
            if (type === 'hcie') {
                g.filepath = savedPath;
                const el = document.getElementById('filePath');
                if (el) el.innerText = savedPath;
            }
        }
    } catch (err) {
        console.error('Save As failed:', err);
    }
}

export async function handleExportFile() {
    try {
        const activeLayer = layers[g.activeLayerIndex];
        if (!activeLayer) return;

        const dataURL = (activeLayer.canvas as HTMLCanvasElement).toDataURL('image/png');
        await api.saveFile(dataURL, g.filepath, true, 'png');
    } catch (err) {
        console.error('Export failed:', err);
    }
}

// --- Edit Operations ---

export function undoImage() {
    historyManager.undo();
}

export function redoImage() {
    historyManager.redo();
}

/**
 * Clears the active layer. If a selection is active, only clears the selected area.
 * Otherwise, clears the entire layer.
 */
export function clearCanvas() {
    const activeLayer = layers[g.activeLayerIndex];
    if (!activeLayer || activeLayer.locked) return;

    const ctx = activeLayer.ctx as CanvasRenderingContext2D;
    
    // Check if there's an active selection
    const isSelectionActive = g.isSelectionActive && g.selectionCanvas;
    
    let oldData: ImageData;
    let newData: ImageData;
    
    if (isSelectionActive) {
        // Get selection bounds
        const selectionCanvas = g.selectionCanvas;
        if (!selectionCanvas) return;
        
        const selectionCtx = selectionCanvas.getContext('2d');
        if (!selectionCtx) return;
        
        const maskData = selectionCtx.getImageData(0, 0, selectionCanvas.width, selectionCanvas.height);
        
        // Find bounds of selection
        let minX = g.image_width, minY = g.image_height, maxX = 0, maxY = 0;
        let found = false;
        for (let y = 0; y < g.image_height; y++) {
            const rowOffset = y * g.image_width * 4;
            for (let x = 0; x < g.image_width; x++) {
                const alphaIndex = rowOffset + x * 4 + 3;
                if (maskData.data[alphaIndex] > 10) {
                    if (x < minX) minX = x;
                    if (x > maxX) maxX = x;
                    if (y < minY) minY = y;
                    if (y > maxY) maxY = y;
                    found = true;
                }
            }
        }
        
        if (!found) return; // No valid selection
        
        const width = maxX - minX + 1;
        const height = maxY - minY + 1;
        
        // Save old data for history (only the selected region)
        oldData = ctx.getImageData(minX, minY, width, height);
        
        // Clear only the selected area using destination-out composite
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        ctx.drawImage(selectionCanvas, minX, minY, width, height, minX, minY, width, height);
        ctx.restore();
        
        // Get new data for history
        newData = ctx.getImageData(minX, minY, width, height);
        
        // Create a DrawAction for the selected region only
        historyManager.push(new DrawAction(g.activeLayerIndex, oldData, newData));
    } else {
        // No selection - clear entire layer (original behavior)
        oldData = ctx.getImageData(0, 0, g.image_width, g.image_height);
        
        ctx.clearRect(0, 0, g.image_width, g.image_height);
        
        newData = ctx.getImageData(0, 0, g.image_width, g.image_height);
        historyManager.push(new DrawAction(g.activeLayerIndex, oldData, newData));
    }
    
    renderLayers();
    updateLayerPanel();
}

// --- Filter Operations ---

export function applyFilter(type: string) {
    const activeLayer = layers[g.activeLayerIndex];
    if (!activeLayer || activeLayer.locked) return;

    const ctx = activeLayer.ctx as CanvasRenderingContext2D;
    const imageData = ctx.getImageData(0, 0, g.image_width, g.image_height);

    switch (type) {
        case 'negative':
        case 'invert':
            filters.applyNegative(imageData);
            break;
        case 'grayscale':
            filters.applyGrayscale(imageData);
            break;
        case 'sepia':
            filters.applySepia(imageData);
            break;
        case 'blur':
            // Using default radius from globals
            const blurred = filters.applyBoxBlur(imageData, g.blur_radius);
            ctx.putImageData(blurred, 0, 0);
            break;
        case 'mosaic':
            const pixelated = filters.applyMosaic(imageData, g.mosaic_block_size);
            ctx.putImageData(pixelated, 0, 0);
            break;
        default:
            console.warn('Unknown filter type:', type);
            return;
    }

    if (type !== 'blur' && type !== 'mosaic') {
        ctx.putImageData(imageData, 0, 0);
    }

    renderLayers();
    updateLayerPanel();
    // TODO: Add to history
}

// --- View Operations ---

export function zoomIn() {
    if ((window as any).zoomIn) {
        (window as any).zoomIn(null);
    }
}

export function zoomOut() {
    if ((window as any).zoomOut) {
        (window as any).zoomOut(null);
    }
}

// --- New Image Dialog ---

export function closeNewImageDialog() {
    const modal = document.getElementById('newImageModal');
    if (modal) modal.style.display = 'none';
}

export function openNewImageDialog() {
     const modal = document.getElementById('newImageModal');
    if (modal) {
        modal.style.display = 'block';
        // Reset to custom or default values if needed
    }
}

export function applyPreset() {
    const preset = (document.getElementById('imagePreset') as HTMLSelectElement).value;
    const widthInput = document.getElementById('newWidth') as HTMLInputElement;
    const heightInput = document.getElementById('newHeight') as HTMLInputElement;

    switch (preset) {
        case 'hd':
            widthInput.value = '1280';
            heightInput.value = '720';
            break;
        case 'fhd':
            widthInput.value = '1920';
            heightInput.value = '1080';
            break;
        case '4k':
            widthInput.value = '3840';
            heightInput.value = '2160';
            break;
        case 'a4':
            widthInput.value = '2480';
            heightInput.value = '3508';
            break;
        case 'square':
            widthInput.value = '1024';
            heightInput.value = '1024';
            break;
        case 'custom':
            // Don't change values
            break;
    }
}

export function setOrientation(orientation: 'portrait' | 'landscape') {
    const widthInput = document.getElementById('newWidth') as HTMLInputElement;
    const heightInput = document.getElementById('newHeight') as HTMLInputElement;
    const width = parseInt(widthInput.value);
    const height = parseInt(heightInput.value);

    const btnPortrait = document.getElementById('btn-portrait');
    const btnLandscape = document.getElementById('btn-landscape');

    if (orientation === 'portrait') {
        if (width > height) {
            widthInput.value = height.toString();
            heightInput.value = width.toString();
        }
        btnPortrait?.classList.add('btn-active');
        btnLandscape?.classList.remove('btn-active');
    } else {
        if (height > width) {
            widthInput.value = height.toString();
            heightInput.value = width.toString();
        }
        btnLandscape?.classList.add('btn-active');
        btnPortrait?.classList.remove('btn-active');
    }
}

export function createNewImage() {
    const width = parseInt((document.getElementById('newWidth') as HTMLInputElement).value);
    const height = parseInt((document.getElementById('newHeight') as HTMLInputElement).value);
    const name = "Untitled"; 
    
    const bgColor = (document.getElementById('newBgColor') as HTMLInputElement).value;
    const isTransparent = (document.getElementById('transparentBg') as HTMLInputElement).checked;

    newDocument(name, width, height);

    // After newDocument, the active layer is the Background layer.
    const activeLayer = layers[g.activeLayerIndex];
    if (activeLayer) {
        const ctx = activeLayer.ctx as CanvasRenderingContext2D;
        ctx.clearRect(0, 0, width, height);
        if (!isTransparent) {
            ctx.fillStyle = bgColor;
            ctx.fillRect(0, 0, width, height);
        }
    }
    
    closeNewImageDialog();
    renderLayers();
    updateLayerPanel();
}

export function exitApp() {
    if ((window as any).__TAURI__) {
        // Tauri exit
        try {
            (window as any).__TAURI__.process.exit(0);
        } catch (e) {
            console.error('Tauri exit failed:', e);
            window.close();
        }
    } else if ((window as any).electronAPI) {
        // Electron exit via API if available
        window.close();
    } else {
        // Web fallback
        if (confirm("Are you sure you want to exit? Unsaved changes will be lost.")) {
            window.location.href = "about:blank";
        }
    }
}

// Expose to window
if (typeof window !== 'undefined') {
    Object.assign(window, {
        handleOpenFile,
        openImage: handleOpenFile, // Alias for legacy HTML
        handleSaveFile,
        saveProject: handleSaveFile, // Alias for legacy HTML
        handleSaveAsFile,
        saveAsProject: handleSaveAsFile, // Alias for legacy HTML
        handleExportFile,
        undoImage,
        redoImage,
        clearCanvas,
        applyFilter,
        zoomIn,
        zoomOut,
        closeNewImageDialog,
        openNewImageDialog,
        newCanvas: openNewImageDialog, // Alias for legacy HTML
        exitApp,
        applyPreset,
        setOrientation,
        createNewImage
    });
}